/**
 * Alternate Blog listing behaviour.
 *
 * Shopify caps a plain article loop at 50, so the section server-renders the first page and
 * this script fetches the rest from the lightweight `?view=cards` template, appends them, and
 * then powers instant search + tag filtering over the COMPLETE set of posts.
 *
 * Self-initializing per .alternate-blog instance (multiple sections / theme-editor re-render).
 */
(function () {
  function initSection(section) {
    const grid = section.querySelector('.blog-grid');
    if (!grid) return;

    const search = section.querySelector('[data-blog-search]');
    const input = search ? search.querySelector('.blog-search-input') : null;
    const clearBtn = search ? search.querySelector('.blog-search-clear') : null;
    const noResults = section.querySelector('.blog-no-results');
    const loadingEl = section.querySelector('[data-blog-loading]');
    const countEl = section.querySelector('.blog-count');
    const tagsNav = section.querySelector('[data-blog-tags]');

    const total = countEl ? parseInt(countEl.getAttribute('data-total'), 10) : 0;
    const pageSize = parseInt(section.getAttribute('data-page-size'), 10) || 50;
    const blogUrl = section.getAttribute('data-blog-url') || '';

    let cards = Array.from(grid.querySelectorAll('.blog-card'));
    let activeTag = '';

    const tagsOf = (card) => (card.getAttribute('data-tags') || '').split('|').filter(Boolean);

    const updateCount = (visible, filtering) => {
      if (!countEl) return;
      countEl.textContent = filtering ? `${visible} of ${total} Articles` : `${total} Articles`;
    };

    const applyFilter = () => {
      const query = input ? input.value.trim().toLowerCase() : '';
      const terms = query.split(/\s+/).filter(Boolean);
      let visible = 0;

      cards.forEach((card) => {
        const haystack = card.getAttribute('data-search') || '';
        const matchSearch = terms.every((term) => haystack.indexOf(term) !== -1);
        const matchTag = activeTag === '' || tagsOf(card).indexOf(activeTag) !== -1;
        const show = matchSearch && matchTag;
        card.hidden = !show;
        if (show) visible++;
      });

      if (clearBtn) clearBtn.hidden = query.length === 0;
      if (noResults) noResults.hidden = visible !== 0;
      updateCount(visible, query.length > 0 || activeTag !== '');
    };

    const refreshTagCounts = () => {
      if (!tagsNav) return;
      const counts = {};
      cards.forEach((card) => {
        tagsOf(card).forEach((tag) => {
          counts[tag] = (counts[tag] || 0) + 1;
        });
      });

      tagsNav.querySelectorAll('.blog-tag').forEach((btn) => {
        const countSpan = btn.querySelector('.blog-tag-count');
        if (!countSpan) return;
        const tag = btn.getAttribute('data-tag') || '';
        countSpan.textContent = tag === '' ? cards.length : counts[tag] || 0;
      });

      // Order tags by count (desc), alphabetical tiebreak; keep "All posts" pinned first.
      const list = tagsNav.querySelector('.blog-tags-list');
      if (!list) return;
      Array.from(list.children)
        .filter((li) => {
          const btn = li.querySelector('.blog-tag');
          return btn && btn.getAttribute('data-tag') !== '';
        })
        .sort((a, b) => {
          const tagA = a.querySelector('.blog-tag').getAttribute('data-tag');
          const tagB = b.querySelector('.blog-tag').getAttribute('data-tag');
          return (counts[tagB] || 0) - (counts[tagA] || 0) || tagA.localeCompare(tagB);
        })
        .forEach((li) => list.appendChild(li));
    };

    const wireTags = () => {
      if (!tagsNav) return;
      tagsNav.querySelectorAll('.blog-tag').forEach((btn) => {
        btn.addEventListener('click', () => {
          activeTag = btn.getAttribute('data-tag') || '';
          tagsNav
            .querySelectorAll('.blog-tag')
            .forEach((other) => other.classList.toggle('is-active', other === btn));
          applyFilter();
        });
      });
    };

    // Fetch posts beyond the first page and append them to the grid.
    const loadRemaining = async () => {
      if (!blogUrl || total <= cards.length) return;
      const pages = Math.ceil(total / pageSize);
      if (pages <= 1) return;

      if (loadingEl) loadingEl.hidden = false;
      const sep = blogUrl.indexOf('?') === -1 ? '?' : '&';

      const requests = [];
      for (let page = 2; page <= pages; page++) {
        requests.push(
          fetch(`${blogUrl}${sep}view=cards&page=${page}`)
            .then((res) => (res.ok ? res.text() : ''))
            .then((html) => ({ page, html }))
            .catch(() => ({ page, html: '' }))
        );
      }

      const results = (await Promise.all(requests)).sort((a, b) => a.page - b.page);
      const parser = new DOMParser();
      const fragment = document.createDocumentFragment();

      results.forEach(({ html }) => {
        if (!html) return;
        const doc = parser.parseFromString(html, 'text/html');
        doc.querySelectorAll('.blog-card').forEach((card) => fragment.appendChild(card));
      });

      grid.appendChild(fragment);
      cards = Array.from(grid.querySelectorAll('.blog-card'));
      if (loadingEl) loadingEl.hidden = true;
    };

    // Wire everything against the first page immediately, then fold in the rest.
    if (input) input.addEventListener('input', applyFilter);
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        input.value = '';
        applyFilter();
        input.focus();
      });
    }
    wireTags();
    refreshTagCounts();
    applyFilter();

    loadRemaining().then(() => {
      refreshTagCounts();
      applyFilter();
    });
  }

  const init = () => {
    document.querySelectorAll('.alternate-blog').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();